import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { UploadDocumentsModal } from "../../contacts/components/UploadDocumentsModal";
import { AddInvoiceJobClosedCheckModal } from "./AddInvoiceJobClosedCheckModal";
import { AddInvoiceModal } from "./AddInvoiceModal";
import { costsService } from "@/api/services/costs.service";
import { documentsService, } from "@/api/services/documents.service";
import { jobsService } from "@/api/services/jobs.service";
import { quotesService } from "@/api/services/quotes.service";
import { xeroService } from "@/api/services/xero.service";
import { formatScopeOfWorksAsBullets, getScopeOfWorksItems, } from "@/utils/quoteHelpers";
import { useToast } from "@/components/common";
import { useAlert } from "@/hooks/useAlert";
import { formatDateOnlyAUS } from "@/utils/dateTime";
import { formatCurrency } from "@/utils/formatters";
import styles from "./JobDocumentsPhotosTab.module.css";
const JOB_FILTER_TABS = [
    { id: "all", label: "All" },
    { id: "permits", label: "Permits" },
    { id: "plans", label: "Plans" },
    { id: "invoices", label: "Invoices" },
    { id: "images", label: "Images" },
    { id: "additional_works", label: "Additional Works" },
    { id: "other", label: "Other" },
];
const SITE_FILTER_TABS = [
    { id: "all", label: "All" },
    { id: "permits", label: "Permits" },
    { id: "plans", label: "Plans" },
    { id: "invoices", label: "Invoices" },
    { id: "images", label: "Images" },
    { id: "other", label: "Other" },
];
const JOB_DOCUMENT_CATEGORIES = [
    "Permits",
    "Plans",
    "Invoices",
    "Images",
    "Additional Works",
    "Other",
];
const SITE_UPLOAD_CATEGORIES = [
    "Permits",
    "Plans",
    "Invoices",
    "Images",
    "Other",
];
const QUOTE_UPLOAD_CATEGORIES = [
    "Permits",
    "Plans",
    "Invoices",
    "Images",
    "Other",
];
const UI_CATEGORY_TO_QUOTE_CATEGORY = {
    permits: "permits",
    plans: "plans",
    invoices: "invoices",
    images: "images",
    other: "other",
};
const formatFileSize = (bytes) => {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};
const formatXeroInvoiceDateCell = (inv) => {
    if (inv.paidDate) {
        return `Paid On ${formatDateOnlyAUS(inv.paidDate)}`;
    }
    return `Created On ${formatDateOnlyAUS(inv.createdAt)}`;
};
const JOB_DOCUMENT_PREFIX = "job document - ";
/** Allowed suffix values (lowercase) for "Job document - X" → UI category. */
const JOB_DOC_SUFFIX_TO_CATEGORY = {
    permits: "permits",
    plans: "plans",
    invoices: "invoices",
    images: "images",
    "additional works": "additional_works",
    other: "other",
};
// Map job API documentType + description to UI category (permits/plans/invoices/images/other)
const jobDocToCategory = (documentType, description) => {
    const t = documentType?.toLowerCase() ?? "";
    if (t === "building_permit" ||
        t === "cfi_certificate" ||
        t === "site_map" ||
        t === "notice_to_vacate" ||
        t === "certificates")
        return "permits";
    if (t === "plans")
        return "plans";
    if (t === "invoices")
        return "invoices";
    if (t === "images")
        return "images";
    if (t !== "other")
        return "other";
    const desc = (description ?? "").toLowerCase();
    // Preferred: parse "Job document - X" case-insensitively so uploads land in correct tab
    if (desc.startsWith(JOB_DOCUMENT_PREFIX)) {
        const suffix = desc.slice(JOB_DOCUMENT_PREFIX.length).trim();
        const category = JOB_DOC_SUFFIX_TO_CATEGORY[suffix];
        if (category)
            return category;
    }
    // Fallback: keyword matching for legacy or non-upload documents
    if (desc.includes("permit"))
        return "permits";
    if (desc.includes("invoice"))
        return "invoices";
    if (desc.includes("plan"))
        return "plans";
    if (desc.includes("image"))
        return "images";
    return "other";
};
const mapJobDocToDisplay = (doc) => {
    const category = jobDocToCategory(doc.documentType, doc.description);
    return {
        id: doc.id,
        name: doc.name || doc.fileName,
        fileSize: formatFileSize(doc.fileSize),
        fileType: doc.mimeType.split("/").pop()?.toUpperCase() || "FILE",
        date: formatDateOnlyAUS(doc.createdAt),
        uploadedBy: doc.uploadedByName || "Unknown",
        uploadedByInitial: doc.uploadedByName?.charAt(0).toUpperCase() || "?",
        category,
        apiCategory: "job",
        siteCategory: category,
        description: doc.description || null,
        source: "job",
        jobApiSource: "jobs",
    };
};
// Map site API documents to display format (use siteCategory only; category is for contacts)
const mapApiDocToDisplay = (doc) => {
    const siteCategory = (doc.siteCategory ?? "").toString().toLowerCase();
    const category = (doc.siteCategory ?? "other").toString().toLowerCase();
    const apiCategory = (doc.category ?? "").toString().toLowerCase();
    return {
        id: doc.id,
        name: doc.name || doc.fileName,
        fileSize: doc.fileSizeFormatted,
        fileType: doc.mimeType.split("/").pop()?.toUpperCase() || "FILE",
        date: formatDateOnlyAUS(doc.createdAt),
        uploadedBy: doc.uploadedByName || "Unknown",
        uploadedByInitial: doc.uploadedByName?.charAt(0).toUpperCase() || "?",
        category,
        apiCategory,
        siteCategory,
        description: doc.description || null,
        source: "site",
    };
};
// Map quote API documents to display format (source: quote).
const mapQuoteDocToDisplay = (doc) => {
    const rawCategory = doc.quoteCategory ??
        doc.jobCategory ??
        doc.siteCategory ??
        "other";
    const category = String(rawCategory).trim().toLowerCase();
    const apiCategory = (doc.category ?? "").toString().toLowerCase();
    const siteCategory = (doc.siteCategory ?? "").toString().toLowerCase();
    return {
        id: doc.id,
        name: doc.name || doc.fileName,
        fileSize: doc.fileSizeFormatted,
        fileType: doc.mimeType.split("/").pop()?.toUpperCase() || "FILE",
        date: formatDateOnlyAUS(doc.createdAt),
        uploadedBy: doc.uploadedByName || "Unknown",
        uploadedByInitial: doc.uploadedByName?.charAt(0).toUpperCase() || "?",
        category,
        apiCategory,
        siteCategory,
        description: doc.description || null,
        source: "quote",
    };
};
// Map job documents from /api/documents/job/{jobId} to display format (source: job, jobApiSource: documents).
// Use jobCategory instead of category; all string comparisons case-insensitive.
const mapJobDocumentFromDocumentsApiToDisplay = (doc) => {
    const rawCategory = doc.jobCategory ?? doc.siteCategory ?? doc.category ?? "other";
    const category = String(rawCategory).trim().toLowerCase();
    const apiCategory = (doc.category ?? "").toString().toLowerCase();
    const siteCategory = (doc.siteCategory ?? "").toString().toLowerCase();
    return {
        id: doc.id,
        name: doc.name || doc.fileName,
        fileSize: doc.fileSizeFormatted,
        fileType: doc.mimeType.split("/").pop()?.toUpperCase() || "FILE",
        date: formatDateOnlyAUS(doc.createdAt),
        uploadedBy: doc.uploadedByName || "Unknown",
        uploadedByInitial: doc.uploadedByName?.charAt(0).toUpperCase() || "?",
        category,
        apiCategory,
        siteCategory,
        description: doc.description || null,
        source: "job",
        jobApiSource: "documents",
    };
};
const UI_CATEGORY_TO_JOB_CATEGORY = {
    permits: "permits",
    plans: "plans",
    invoices: "invoices",
    images: "images",
    additional_works: "additional_works",
    other: "other",
};
export const JobDocumentsPhotosTab = ({ jobId, job, jobDocuments: jobDocumentsFromApi, jobDocumentsFromDocumentsApi = [], siteDocuments: siteDocumentsFromApi, quoteDocuments: quoteDocumentsFromApi = [], refetchJobDocuments, refetchJobDocumentsFromDocumentsApi, refetchSiteDocuments, refetchQuoteDocuments, openAddDocumentDefaults, onOpenAddDocumentConsumed, refetchJob, updateJob, }) => {
    const { error: showError, success, warning } = useToast();
    const { confirm } = useAlert();
    const pendingUploadDescriptionRef = useRef(undefined);
    const [addDocumentDefaultCategory, setAddDocumentDefaultCategory] = useState(null);
    // Job Documents: merged from GET api/jobs/{id}/documents and GET api/documents/job/{jobId}
    const jobDocumentsFromJobsApi = useMemo(() => jobDocumentsFromApi.map(mapJobDocToDisplay), [jobDocumentsFromApi]);
    const jobDocumentsFromDocsApi = useMemo(() => jobDocumentsFromDocumentsApi.map(mapJobDocumentFromDocumentsApiToDisplay), [jobDocumentsFromDocumentsApi]);
    const jobDocuments = useMemo(() => [...jobDocumentsFromJobsApi, ...jobDocumentsFromDocsApi], [jobDocumentsFromJobsApi, jobDocumentsFromDocsApi]);
    const siteDocuments = useMemo(() => siteDocumentsFromApi.map(mapApiDocToDisplay), [siteDocumentsFromApi]);
    const quoteDocuments = useMemo(() => quoteDocumentsFromApi.map(mapQuoteDocToDisplay), [quoteDocumentsFromApi]);
    const siteId = job?.siteId ?? null;
    const siteName = job?.siteName ?? null;
    const [jobDocsFilter, setJobDocsFilter] = useState("all");
    const [finalFilesFilter, setFinalFilesFilter] = useState("all");
    const [quoteDocsFilter, setQuoteDocsFilter] = useState("all");
    const [finalDocsFilter, setFinalDocsFilter] = useState("all");
    const [jobDocsSearch, setJobDocsSearch] = useState("");
    const [finalFilesSearch, setFinalFilesSearch] = useState("");
    const [quoteDocsSearch, setQuoteDocsSearch] = useState("");
    const [finalDocsSearch, setFinalDocsSearch] = useState("");
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isSiteUploadModalOpen, setIsSiteUploadModalOpen] = useState(false);
    const [isSiteUploading, setIsSiteUploading] = useState(false);
    const [isQuoteUploadModalOpen, setIsQuoteUploadModalOpen] = useState(false);
    const [isQuoteUploading, setIsQuoteUploading] = useState(false);
    const [isAddInvoiceModalOpen, setIsAddInvoiceModalOpen] = useState(false);
    const [isJobClosedCheckModalOpen, setIsJobClosedCheckModalOpen] = useState(false);
    const [isUpdatingJobStatus, setIsUpdatingJobStatus] = useState(false);
    const [invoiceDefaultAmount, setInvoiceDefaultAmount] = useState(undefined);
    const [invoiceDefaultDescription, setInvoiceDefaultDescription] = useState(undefined);
    const [invoiceDefaultScopeItems, setInvoiceDefaultScopeItems] = useState([]);
    const [jobInvoices, setJobInvoices] = useState([]);
    const [jobInvoicesLoading, setJobInvoicesLoading] = useState(false);
    const fetchJobInvoices = useCallback(async () => {
        const contactId = job?.contactId;
        if (!contactId || !jobId) {
            setJobInvoices([]);
            return;
        }
        setJobInvoicesLoading(true);
        try {
            const response = await xeroService.getInvoicesByContact(contactId);
            const data = response.data;
            if (data.success && data.invoices) {
                const visible = data.invoices.filter((inv) => inv.jobId === jobId);
                setJobInvoices(visible);
            }
            else {
                setJobInvoices([]);
            }
        }
        catch (err) {
            console.error("Error fetching job invoices:", err);
            setJobInvoices([]);
        }
        finally {
            setJobInvoicesLoading(false);
        }
    }, [job?.contactId, jobId]);
    useEffect(() => {
        fetchJobInvoices();
    }, [fetchJobInvoices]);
    const handleViewInvoice = useCallback(async (invoice) => {
        const linkId = invoice.xeroInvoiceId ?? invoice.id;
        try {
            const response = await xeroService.getInvoiceLink(linkId);
            const data = response.data;
            if (data.success && data.link) {
                window.open(data.link, "_blank");
            }
            else {
                showError(data.error ?? "Failed to get invoice link.");
            }
        }
        catch (err) {
            console.error("Error fetching invoice link:", err);
            showError("Failed to get invoice link.");
        }
    }, [showError]);
    useEffect(() => {
        if (!openAddDocumentDefaults)
            return;
        setAddDocumentDefaultCategory(openAddDocumentDefaults.category);
        pendingUploadDescriptionRef.current = openAddDocumentDefaults.description;
        setIsUploadModalOpen(true);
        onOpenAddDocumentConsumed?.();
    }, [openAddDocumentDefaults, onOpenAddDocumentConsumed]);
    useEffect(() => {
        if (!isAddInvoiceModalOpen) {
            setInvoiceDefaultAmount(undefined);
            setInvoiceDefaultDescription(undefined);
            setInvoiceDefaultScopeItems([]);
            return;
        }
        if (job?.quoteId) {
            Promise.all([
                quotesService.getQuote(job.quoteId),
                costsService.getGroups(),
            ])
                .then(([quoteRes, groupsRes]) => {
                const quote = quoteRes.data;
                const costGroups = groupsRes.data ?? [];
                const subTotal = quote.subTotal ?? 0;
                const buildersMargin = quote.buildersMarginEnabled && quote.buildersMarginAmount
                    ? quote.buildersMarginAmount
                    : 0;
                const totalIncGst = (subTotal + buildersMargin) * 1.1;
                setInvoiceDefaultAmount(Math.round(totalIncGst * 100) / 100);
                const scopeItems = getScopeOfWorksItems(quote.lineItems, costGroups);
                setInvoiceDefaultScopeItems(scopeItems);
                const bulletedScope = formatScopeOfWorksAsBullets(scopeItems);
                let defaultDesc;
                if (bulletedScope.length > 0) {
                    const provisionBlock = `The provision of defit services including:\n${bulletedScope}`;
                    const scopePrefix = (job?.scope ?? "").trim();
                    defaultDesc = scopePrefix
                        ? `${scopePrefix}\n${provisionBlock}`
                        : provisionBlock;
                }
                else {
                    defaultDesc = quote.jobDescription ?? job?.scope ?? "";
                }
                const suffix = job?.jobNumber ? ` (Job# ${job.jobNumber})` : "";
                setInvoiceDefaultDescription(((defaultDesc || "").trim() + suffix).trim() || undefined);
            })
                .catch(() => {
                setInvoiceDefaultAmount(job?.estimatedCost ?? undefined);
                const base = job?.scope ?? "";
                const suffix = job?.jobNumber ? ` (Job# ${job.jobNumber})` : "";
                setInvoiceDefaultDescription(((base || "").trim() + suffix).trim() || undefined);
                setInvoiceDefaultScopeItems([]);
            });
        }
        else {
            setInvoiceDefaultAmount(job?.estimatedCost ?? undefined);
            const base = job?.scope ?? "";
            const suffix = job?.jobNumber ? ` (Job# ${job.jobNumber})` : "";
            setInvoiceDefaultDescription(((base || "").trim() + suffix).trim() || undefined);
            setInvoiceDefaultScopeItems([]);
        }
    }, [
        isAddInvoiceModalOpen,
        job?.quoteId,
        job?.estimatedCost,
        job?.scope,
        job?.jobNumber,
        job,
    ]);
    const handleDownload = async (doc) => {
        try {
            let response;
            if (doc.source === "job" && doc.jobApiSource === "documents") {
                response = await documentsService.getDownloadUrl(doc.id);
            }
            else if (doc.source === "job") {
                response = await jobsService.getJobDocumentDownloadUrl(doc.id);
            }
            else {
                response = await documentsService.getDownloadUrl(doc.id);
            }
            window.open(response.data.url, "_blank");
        }
        catch (err) {
            console.error("Failed to get download URL:", err);
            showError("Failed to download document. Please try again.");
        }
    };
    const handleDelete = async (doc) => {
        const confirmed = await confirm("Are you sure you want to delete this document?");
        if (!confirmed)
            return;
        try {
            if (doc.source === "job" && doc.jobApiSource === "documents") {
                await documentsService.deleteDocument(doc.id);
                await refetchJobDocumentsFromDocumentsApi?.();
            }
            else if (doc.source === "job") {
                await jobsService.deleteJobDocument(doc.id);
                await refetchJobDocuments();
            }
            else if (doc.source === "quote") {
                await documentsService.deleteDocument(doc.id);
                await refetchQuoteDocuments?.();
            }
            else {
                await documentsService.deleteDocument(doc.id);
                await refetchSiteDocuments();
            }
        }
        catch (err) {
            console.error("Failed to delete document:", err);
            showError("Failed to delete document. Please try again.");
        }
    };
    const handleAddDocuments = (section) => {
        if (section === "quote") {
            setIsQuoteUploadModalOpen(true);
            return;
        }
        if (section === "other") {
            setIsSiteUploadModalOpen(true);
            return;
        }
        setIsUploadModalOpen(true);
    };
    const handleSaveDocuments = async (uploadedFiles, category) => {
        if (!jobId) {
            warning("Job not found. Please go back and open the job again.");
            setIsUploadModalOpen(false);
            return;
        }
        const key = (category ?? "").trim().toLowerCase().replace(/\s+/g, "_");
        const jobCategory = UI_CATEGORY_TO_JOB_CATEGORY[key] ?? "other";
        const description = pendingUploadDescriptionRef.current;
        try {
            for (const uploadedFile of uploadedFiles) {
                if (!uploadedFile.file) {
                    throw new Error(`File object missing for ${uploadedFile.name}`);
                }
                await documentsService.uploadJobDocument(uploadedFile.file, {
                    jobId,
                    name: uploadedFile.name,
                    jobCategory,
                    ...(description && { description }),
                });
            }
            pendingUploadDescriptionRef.current = undefined;
            await refetchJobDocuments();
            await refetchJobDocumentsFromDocumentsApi?.();
            setIsUploadModalOpen(false);
            setAddDocumentDefaultCategory(null);
        }
        catch (err) {
            console.error("Failed to upload documents:", err);
            showError("Failed to upload documents. Please try again.");
        }
    };
    const handleSaveSiteDocuments = async (uploadedFiles, selectedSiteCategory) => {
        if (!siteId) {
            warning("No site is linked to this job. Link a site to upload site documents.");
            setIsSiteUploadModalOpen(false);
            return;
        }
        const siteCategoryMap = {
            Permits: "permits",
            Plans: "plans",
            Invoices: "invoices",
            Images: "images",
            Other: "other",
        };
        const siteCategory = siteCategoryMap[selectedSiteCategory] || "other";
        setIsSiteUploading(true);
        try {
            for (const fileInfo of uploadedFiles) {
                if (!fileInfo.file) {
                    throw new Error(`File object missing for ${fileInfo.name}`);
                }
                await documentsService.uploadDocument(fileInfo.file, {
                    siteId,
                    siteCategory,
                    name: fileInfo.name.split(".")[0],
                });
            }
            await refetchSiteDocuments();
            setIsSiteUploadModalOpen(false);
        }
        catch (err) {
            console.error("Failed to upload site documents:", err);
            const message = err && typeof err === "object" && "response" in err
                ? err.response
                    ?.data?.message
                : null;
            showError(message
                ? `Failed to upload documents: ${message}`
                : "Failed to upload documents. Please try again.");
        }
        finally {
            setIsSiteUploading(false);
        }
    };
    const handleSaveQuoteDocuments = async (uploadedFiles, category) => {
        const quoteId = job?.quoteId;
        if (!quoteId) {
            warning("No quote is linked to this job. Cannot upload quote documents.");
            setIsQuoteUploadModalOpen(false);
            return;
        }
        const key = (category ?? "").trim().toLowerCase().replace(/\s+/g, "_");
        const quoteCategory = UI_CATEGORY_TO_QUOTE_CATEGORY[key] ?? "other";
        setIsQuoteUploading(true);
        try {
            for (const uploadedFile of uploadedFiles) {
                if (!uploadedFile.file) {
                    throw new Error(`File object missing for ${uploadedFile.name}`);
                }
                await documentsService.uploadQuoteDocument(uploadedFile.file, {
                    quoteId,
                    name: uploadedFile.name,
                    quoteCategory,
                });
            }
            await refetchQuoteDocuments?.();
            setIsQuoteUploadModalOpen(false);
        }
        catch (err) {
            console.error("Failed to upload quote documents:", err);
            showError("Failed to upload documents. Please try again.");
        }
        finally {
            setIsQuoteUploading(false);
        }
    };
    const handleAddInvoice = async () => {
        if (jobInvoices.length > 0) {
            const ok = await confirm("Are you sure you want to send another invoice to the Client for this job?");
            if (!ok)
                return;
        }
        const status = job?.status;
        if (status === "completed" || status === "invoiced") {
            setIsAddInvoiceModalOpen(true);
        }
        else {
            setIsJobClosedCheckModalOpen(true);
        }
    };
    const handleJobClosedCheckUpdateToComplete = async () => {
        if (!jobId)
            return;
        setIsUpdatingJobStatus(true);
        try {
            await jobsService.updateJobStatus(jobId, "completed");
            await refetchJob?.();
            setIsJobClosedCheckModalOpen(false);
            setIsAddInvoiceModalOpen(true);
        }
        catch (err) {
            console.error("Failed to update job status:", err);
            showError("Failed to update job status. Please try again.");
        }
        finally {
            setIsUpdatingJobStatus(false);
        }
    };
    const handleJobClosedCheckProceedToInvoice = async (reason) => {
        setIsJobClosedCheckModalOpen(false);
        if (reason.trim() && jobId) {
            try {
                await jobsService.updateJob(jobId, { remarks: reason.trim() });
                await refetchJob?.();
            }
            catch (err) {
                console.error("Failed to save reason for early invoice:", err);
                showError("Failed to save reason. Proceeding to invoice anyway.");
            }
        }
        setIsAddInvoiceModalOpen(true);
    };
    const handleSaveInvoice = async (invoiceData) => {
        const contactId = job?.contactId;
        if (!contactId) {
            warning("No contact is linked to this job. Cannot create invoice.");
            return;
        }
        if (!jobId) {
            warning("Job not found. Cannot create invoice.");
            return;
        }
        try {
            await xeroService.createInvoice({
                contactId,
                amount: invoiceData.invoiceAmount,
                invoiceDescription: invoiceData.description || "",
                jobId,
                scopeItems: invoiceDefaultScopeItems.length > 0
                    ? invoiceDefaultScopeItems
                    : undefined,
            });
            await jobsService.updateJobStatus(jobId, "invoiced");
            await updateJob({ status: "invoiced" });
            success("Invoice created successfully.");
            setIsAddInvoiceModalOpen(false);
            await fetchJobInvoices();
            await refetchJob?.();
        }
        catch (err) {
            console.error("Failed to create invoice:", err);
            const message = err && typeof err === "object" && "response" in err
                ? err.response
                    ?.data?.message
                : null;
            showError(message
                ? `Failed to create invoice: ${message}`
                : "Failed to create invoice. Please try again.");
        }
    };
    // Filter documents by search term
    const filterBySearch = (docs, search) => {
        if (!search.trim())
            return docs;
        const searchLower = search.toLowerCase();
        return docs.filter((doc) => doc.name.toLowerCase().includes(searchLower) ||
            doc.uploadedBy.toLowerCase().includes(searchLower));
    };
    const filteredJobDocs = filterBySearch(jobDocuments, jobDocsSearch);
    const filteredSiteDocs = filterBySearch(siteDocuments, finalFilesSearch);
    const filteredQuoteDocs = filterBySearch(quoteDocuments, quoteDocsSearch);
    const normalizeCategory = (doc) => (doc.category ?? "").toString().trim().toLowerCase();
    const filterJobDocumentsByCategory = (docs, filter) => {
        const filterLower = filter === "all" ? "" : filter.trim().toLowerCase();
        const isPermitDocument = (doc) => {
            if (normalizeCategory(doc) === "permits")
                return true;
            const desc = doc.description?.toLowerCase() || "";
            const siteCat = (doc.siteCategory ?? "").toString().toLowerCase();
            if (siteCat === "other" && desc.includes("permits"))
                return true;
            if (!desc.includes("building permit"))
                return false;
            if (desc.includes("site map"))
                return false;
            return (desc.includes("notice to vacate") ||
                desc.includes("other") ||
                (siteCat === "other" && desc.includes("building permit")));
        };
        if (filter === "all")
            return docs;
        switch (filterLower) {
            case "permits":
                return docs.filter(isPermitDocument);
            case "plans":
                return docs.filter((doc) => normalizeCategory(doc) === "plans");
            case "invoices":
                return docs.filter((doc) => normalizeCategory(doc) === "invoices" ||
                    normalizeCategory(doc) === "invoice" ||
                    ((doc.siteCategory ?? "").toString().toLowerCase() === "other" &&
                        (doc.description?.toLowerCase().includes("invoice") ?? false)));
            case "images":
                return docs.filter((doc) => normalizeCategory(doc) === "images");
            case "additional_works":
                return docs.filter((doc) => normalizeCategory(doc) === "additional_works");
            case "other":
                return docs.filter((doc) => normalizeCategory(doc) === "other");
            default:
                return docs;
        }
    };
    const filterSiteDocumentsByCategory = (docs, filter) => {
        if (filter === "all")
            return docs;
        const filterLower = filter.trim().toLowerCase();
        return docs.filter((doc) => {
            const siteCategory = (doc.siteCategory || normalizeCategory(doc) || "")
                .toString()
                .trim()
                .toLowerCase();
            if (filterLower === "other") {
                return (siteCategory !== "plans" &&
                    siteCategory !== "images" &&
                    siteCategory !== "permits" &&
                    siteCategory !== "invoices");
            }
            return siteCategory === filterLower;
        });
    };
    const renderDocumentSection = ({ title, documents, activeFilter, setActiveFilter, searchTerm, setSearchTerm, section, showAddButton, filterTabs, filterDocuments, appendRows, }) => {
        const filteredDocuments = filterDocuments(documents, activeFilter);
        return (_jsxs("div", { className: styles.section, children: [_jsx("h2", { className: styles.sectionTitle, children: title }), _jsxs("div", { className: styles.filterTabs, children: [_jsx("div", { children: filterTabs.map((tab) => (_jsx("button", { className: `${styles.filterTab} ${activeFilter === tab.id ? styles.active : ""}`, onClick: () => setActiveFilter(tab.id), children: tab.label }, tab.id))) }), _jsxs("div", { className: styles.header, children: [_jsxs("div", { className: styles.searchWrapper, children: [_jsxs("svg", { className: styles.searchIcon, width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("path", { d: "m21 21-4.35-4.35" })] }), _jsx("input", { type: "text", className: styles.searchInput, placeholder: "Search", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) })] }), showAddButton && (_jsx("div", { className: styles.headerActions, children: _jsx("button", { className: styles.addButton, onClick: () => handleAddDocuments(section), children: "Add Documents" }) }))] })] }), _jsx("div", { className: styles.tableWrapper, children: _jsxs("table", { className: styles.table, children: [_jsx("thead", { children: _jsxs("tr", { className: styles.tableHeader, children: [_jsx("th", { children: "NAME" }), _jsx("th", { children: "DATE" }), _jsx("th", { children: "UPLOADED BY" })] }) }), _jsx("tbody", { children: filteredDocuments.length === 0 && !appendRows ? (_jsx("tr", { children: _jsx("td", { colSpan: 3, className: styles.emptyState, children: _jsxs("div", { className: styles.emptyStateContent, children: [_jsxs("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [_jsx("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }), _jsx("polyline", { points: "14 2 14 8 20 8" })] }), _jsx("p", { children: "No documents found" })] }) }) })) : (_jsxs(_Fragment, { children: [filteredDocuments.map((doc) => (_jsxs("tr", { className: styles.tableRow, children: [_jsx("td", { children: _jsx("div", { className: styles.documentName, children: _jsxs("div", { className: styles.documentInfo, children: [_jsx("span", { className: styles.name, children: doc.name }), _jsxs("span", { className: styles.meta, children: [doc.fileSize, " \u2022 ", doc.fileType] })] }) }) }), _jsx("td", { children: _jsx("span", { className: styles.date, children: doc.date }) }), _jsx("td", { children: _jsxs("div", { className: styles.uploadedBy, children: [_jsxs("div", { className: styles.userInfo, children: [_jsx("div", { className: styles.avatarPlaceholder, children: doc.uploadedByInitial }), _jsx("span", { className: styles.userName, children: doc.uploadedBy })] }), _jsxs("div", { className: styles.actions, children: [_jsx("button", { className: styles.actionButton, onClick: () => handleDownload(doc), title: "Download", children: _jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }), _jsx("polyline", { points: "7 10 12 15 17 10" }), _jsx("line", { x1: "12", y1: "15", x2: "12", y2: "3" })] }) }), (doc.source === "job" ||
                                                                        doc.source === "quote") && (_jsx("button", { className: styles.deleteButton, onClick: () => handleDelete(doc), title: "Delete", children: _jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "currentColor", stroke: "none", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("line", { x1: "8", y1: "12", x2: "16", y2: "12", stroke: "white", strokeWidth: "2" })] }) }))] })] }) })] }, `${doc.source}-${doc.jobApiSource ?? (doc.source === "quote" ? "quote" : "jobs")}-${doc.id}`))), appendRows] })) })] }) })] }));
    };
    if (!jobId) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.loadingState, children: "No job selected." }) }));
    }
    return (_jsxs("div", { className: styles.container, children: [!siteId && (_jsx("div", { className: styles.warningBanner, children: "No site is associated with this job. Site documents are not available. Job documents above are stored only for this job." })), _jsx("div", { className: styles.topActionBar, children: _jsxs("button", { className: styles.addInvoiceButton, onClick: handleAddInvoice, children: [_jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "12", y1: "5", x2: "12", y2: "19" }), _jsx("line", { x1: "5", y1: "12", x2: "19", y2: "12" })] }), "Add Invoice"] }) }), renderDocumentSection({
                title: "Job documents",
                documents: filteredJobDocs,
                activeFilter: jobDocsFilter,
                setActiveFilter: setJobDocsFilter,
                searchTerm: jobDocsSearch,
                setSearchTerm: setJobDocsSearch,
                section: "job",
                showAddButton: true,
                filterTabs: JOB_FILTER_TABS,
                filterDocuments: filterJobDocumentsByCategory,
                appendRows: jobDocsFilter === "invoices" || jobDocsFilter === "all" ? (jobInvoicesLoading ? (_jsx("tr", { children: _jsx("td", { colSpan: 3, className: styles.emptyState, children: _jsxs("div", { className: styles.emptyStateContent, children: [_jsx("div", { className: styles.spinner }), _jsx("p", { children: "Loading invoices..." })] }) }) })) : jobInvoices.length > 0 ? (jobInvoices.map((inv) => (_jsxs("tr", { className: styles.tableRow, children: [_jsx("td", { children: _jsx("div", { className: styles.documentName, children: _jsx("div", { className: styles.documentInfo, children: _jsxs("span", { className: styles.name, children: [inv.invoiceNumber, " - ", formatCurrency(inv.total)] }) }) }) }), _jsx("td", { children: _jsx("span", { className: styles.date, children: formatXeroInvoiceDateCell(inv) }) }), _jsx("td", { children: _jsxs("div", { className: styles.uploadedBy, children: [_jsx("div", {}), _jsx("button", { type: "button", className: styles.viewInvoiceButton, onClick: () => handleViewInvoice(inv), children: "View Invoice" })] }) })] }, `invoice-${inv.id}`)))) : undefined) : undefined,
            }), job?.quoteId &&
                renderDocumentSection({
                    title: "Quote documents",
                    documents: filteredQuoteDocs,
                    activeFilter: quoteDocsFilter,
                    setActiveFilter: setQuoteDocsFilter,
                    searchTerm: quoteDocsSearch,
                    setSearchTerm: setQuoteDocsSearch,
                    section: "quote",
                    showAddButton: true,
                    filterTabs: SITE_FILTER_TABS,
                    filterDocuments: filterSiteDocumentsByCategory,
                }), renderDocumentSection({
                title: `Site documents – ${siteName || "Site"}`,
                documents: filteredSiteDocs,
                activeFilter: finalFilesFilter,
                setActiveFilter: setFinalFilesFilter,
                searchTerm: finalFilesSearch,
                setSearchTerm: setFinalFilesSearch,
                section: "other",
                showAddButton: !!siteId,
                filterTabs: SITE_FILTER_TABS,
                filterDocuments: filterSiteDocumentsByCategory,
            }), _jsx("div", { className: styles.hidden, children: renderDocumentSection({
                    title: "Final documents",
                    documents: [],
                    activeFilter: finalDocsFilter,
                    setActiveFilter: setFinalDocsFilter,
                    searchTerm: finalDocsSearch,
                    setSearchTerm: setFinalDocsSearch,
                    section: "other",
                    showAddButton: false,
                    filterTabs: JOB_FILTER_TABS,
                    filterDocuments: filterJobDocumentsByCategory,
                }) }), _jsx(UploadDocumentsModal, { isOpen: isUploadModalOpen, onClose: () => {
                    setIsUploadModalOpen(false);
                    setAddDocumentDefaultCategory(null);
                    pendingUploadDescriptionRef.current = undefined;
                }, onSave: handleSaveDocuments, categories: JOB_DOCUMENT_CATEGORIES, defaultCategory: addDocumentDefaultCategory ?? undefined }), _jsx(UploadDocumentsModal, { isOpen: isSiteUploadModalOpen, onClose: () => setIsSiteUploadModalOpen(false), onSave: handleSaveSiteDocuments, categories: SITE_UPLOAD_CATEGORIES, categoryLabel: "SITE CATEGORY", isUploading: isSiteUploading }), _jsx(UploadDocumentsModal, { isOpen: isQuoteUploadModalOpen, onClose: () => setIsQuoteUploadModalOpen(false), onSave: handleSaveQuoteDocuments, categories: QUOTE_UPLOAD_CATEGORIES, defaultCategory: "Other", isUploading: isQuoteUploading }), _jsx(AddInvoiceJobClosedCheckModal, { isOpen: isJobClosedCheckModalOpen, onClose: () => setIsJobClosedCheckModalOpen(false), onProceedToInvoice: handleJobClosedCheckProceedToInvoice, onUpdateToComplete: handleJobClosedCheckUpdateToComplete, isUpdating: isUpdatingJobStatus, defaultReason: job?.remarks ?? undefined }), _jsx(AddInvoiceModal, { isOpen: isAddInvoiceModalOpen, onClose: () => setIsAddInvoiceModalOpen(false), onSave: handleSaveInvoice, defaultAmount: invoiceDefaultAmount, defaultDescription: invoiceDefaultDescription })] }));
};
